import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Gift, Percent, CheckCircle, ShoppingCart, ExternalLink, Package, FileText,
  Phone, MessageCircle, Mountain, Crown
} from "lucide-react";

const SUMMIT_PHONE_DISPLAY = "+1 (718) 717-3499";
const SUMMIT_PHONE_TEL = "+17187173499";
const SUMMIT_WHATSAPP = "https://wa.me/17187173499?text=Hi%20Andre%2C%20I%27m%20interested%20in%20Summit%20Peptides%20with%20the%20discount%20from%20PlanMyPeptides.";
const SUMMIT_FLYER_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_a398a994-555b-405c-8664-a909af631619/artifacts/wvcnz9rm_summit_peptides_EN.webp";

const SUMMIT_DISCOUNT_PREMIUM = 0.30;
const SUMMIT_DISCOUNT_FREE = 0.10;

const round2 = (n) => Math.round(n * 100) / 100;

export function BuyTab({ cycleData, handleAffiliateClick, isPremium = false, onUpgradeClick }) {
  const summitDiscount = isPremium ? SUMMIT_DISCOUNT_PREMIUM : SUMMIT_DISCOUNT_FREE;
  const summitDiscountPct = Math.round(summitDiscount * 100);

  const trackOnly = (vendor) => {
    try { handleAffiliateClick?.(vendor, null, { trackOnly: true }); } catch (e) { console.warn(e); }
  };

  const regularTotal = round2(cycleData.total_cost_usd || 0);
  const summitTotal = round2(regularTotal * (1 - summitDiscount));
  const savings = round2(regularTotal - summitTotal);

  const summitPeptides = (cycleData.peptides || []).map((p) => {
    const originalSubtotal = round2((p.cost_per_vial_usd || 0) * (p.vials_needed || 1));
    const discounted = round2((p.cost_per_vial_usd || 0) * (1 - summitDiscount));
    const summitSubtotal = round2(originalSubtotal * (1 - summitDiscount));
    return {
      ...p,
      original_subtotal: originalSubtotal,
      summit_cost_per_vial_usd: discounted,
      summit_total_cost_usd: summitSubtotal,
    };
  });

  return (
    <div className="space-y-6" data-testid="buy-content">
      <Card className="bg-gradient-to-r from-green-500/20 to-cyan-500/20 border-green-500/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
              <Gift className="w-7 h-7 text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Percent className="w-5 h-5 text-green-400" />
                Our Trusted Vendor — Summit Peptides
              </h3>
              <p className="text-zinc-300 mt-1">
                Order directly from <span className="text-amber-400 font-semibold">Summit Peptides</span> for
                {" "}<span className="text-amber-400 font-bold">{summitDiscountPct}% OFF</span>
                {isPremium ? (
                  <span className="text-yellow-300"> (Premium member benefit)</span>
                ) : (
                  <> — <button onClick={onUpgradeClick} className="text-yellow-300 underline underline-offset-2 hover:text-yellow-200" data-testid="upgrade-inline-link">upgrade to Premium for 30% off</button></>
                )}
                .
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="max-w-2xl mx-auto w-full">
        {/* Summit Peptides */}
        <Card className="bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-transparent border-amber-500/40 card-hover" data-testid="summit-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Mountain className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Summit Peptides</h3>
                <p className="text-sm text-zinc-400">Premium Peptides · Pure Results</p>
              </div>
            </div>

            <div className="mb-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/40" data-testid="summit-discount-banner">
              <p className="text-amber-400 font-bold text-sm flex items-center gap-2">
                <Percent className="w-4 h-4" />
                {isPremium ? (
                  <>30% OFF — Premium member exclusive</>
                ) : (
                  <>10% OFF — free tier · <span className="text-yellow-300">upgrade to Premium for 30%</span></>
                )}
              </p>
              {!isPremium && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-2 text-xs text-yellow-300 flex items-center gap-1 hover:text-yellow-200"
                  data-testid="summit-upgrade-cta"
                >
                  <Crown className="w-3 h-3" /> Get 30% off — see plans
                </button>
              )}
            </div>

            <ul className="space-y-2 mb-4 text-sm text-zinc-300">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-400" />High purity &amp; rigorous testing</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-400" />Discreet shipping</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-400" />Talk directly to <span className="text-white font-semibold">Andre Pereira</span></li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-400" />WhatsApp or Call · planmypeptides.com</li>
            </ul>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <Button
                asChild
                className="w-full bg-green-600 hover:bg-green-500 text-white font-bold h-11"
                data-testid="summit-whatsapp-btn"
              >
                <a
                  href={SUMMIT_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackOnly('summit')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-11"
                data-testid="summit-call-btn"
              >
                <a href={`tel:${SUMMIT_PHONE_TEL}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </a>
              </Button>
            </div>

            <p className="text-center text-xs text-zinc-500 mb-3">
              {SUMMIT_PHONE_DISPLAY}
            </p>

            <Button
              asChild
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold h-11"
              data-testid="summit-flyer-btn"
            >
              <a
                href={SUMMIT_FLYER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOnly('summit_flyer')}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Full Product Flyer
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {cycleData.starter_purchase_plan && (
        <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30 mt-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-400" />
              Starter Protocol – Buy Week by Week
            </CardTitle>
            <p className="text-sm text-zinc-400 mt-1">
              {cycleData.starter_purchase_plan.description || "Start your protocol with minimal upfront cost"}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {cycleData.starter_purchase_plan.weeks?.map((week) => (
                <div
                  key={`week-${week.week}`}
                  className={`p-4 rounded-lg border ${
                    week.peptides_to_buy?.length > 0
                      ? 'bg-zinc-900/50 border-green-500/30'
                      : 'bg-zinc-950/50 border-zinc-800'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-white">Week {week.week}</span>
                    <Badge variant={week.estimated_cost > 0 ? "default" : "secondary"}>
                      {week.estimated_cost > 0 ? `$${week.estimated_cost}` : "No purchase needed"}
                    </Badge>
                  </div>
                  {week.peptides_to_buy?.length > 0 ? (
                    <ul className="space-y-1">
                      {week.peptides_to_buy.map((item, i) => (
                        <li key={`buy-${i}-${item}`} className="text-sm text-zinc-300 flex items-center gap-2">
                          <Package className="w-3 h-3 text-cyan-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-zinc-500">Continue with existing supplies</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
              <div className="flex justify-between items-center">
                <span className="text-green-400 font-semibold">Minimum to Start:</span>
                <span className="text-2xl font-bold text-green-400">
                  ${cycleData.starter_purchase_plan.minimum_start_cost || cycleData.starter_purchase_plan.weeks?.[0]?.estimated_cost || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                Instead of buying everything upfront (${cycleData.total_cost_usd}), start with just Week 1 supplies!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        {/* Summit Peptides Shopping List (tiered discount) */}
        <Card className="bg-gradient-to-br from-amber-500/5 to-transparent border-amber-500/30" data-testid="summit-shopping-list">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mountain className="w-5 h-5 text-amber-400" />
              Summit Peptides — {summitDiscountPct}% OFF
              <Badge className="ml-auto bg-amber-500/20 text-amber-400 border-amber-500/40" data-testid="summit-discount-badge">
                SAVE {summitDiscountPct}%
              </Badge>
            </CardTitle>
            {!isPremium && (
              <p className="text-xs text-yellow-300/90 mt-2">
                ★ Upgrade to Premium to unlock <span className="font-bold">30% OFF</span> on Summit Peptides.
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="text-left text-zinc-300">Peptide</th>
                    <th className="text-center text-zinc-300">Vials</th>
                    <th className="text-center text-zinc-300">Price/Vial</th>
                    <th className="text-right text-zinc-300">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {summitPeptides.map((peptide) => (
                    <tr key={`sp-${peptide.name}`}>
                      <td className="text-white font-medium">{peptide.name}</td>
                      <td className="text-center text-zinc-300">{peptide.vials_needed}</td>
                      <td className="text-center">
                        <span className="text-amber-400 font-semibold">${peptide.summit_cost_per_vial_usd}</span>
                        <span className="block text-xs text-zinc-500 line-through">${peptide.cost_per_vial_usd}</span>
                      </td>
                      <td className="text-right">
                        <span className="block text-amber-400 font-bold">${peptide.summit_total_cost_usd}</span>
                        <span className="block text-xs text-zinc-500 line-through">${peptide.original_subtotal}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber-500/30">
                    <td colSpan="3" className="text-right font-bold text-white">Total ({summitDiscountPct}% OFF):</td>
                    <td className="text-right">
                      <span className="block text-2xl font-bold text-amber-400" data-testid="summit-total">${summitTotal}</span>
                      <span className="block text-xs text-zinc-500 line-through">${regularTotal}</span>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="pt-2 text-right text-sm text-green-400 font-semibold" data-testid="summit-savings">
                      You save ${savings}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 pt-3 border-t border-amber-500/20 text-center">
              <p className="text-xs text-zinc-400 mb-2">To order at {summitDiscountPct}% off, contact:</p>
              <p className="text-amber-400 font-bold">Andre Pereira · {SUMMIT_PHONE_DISPLAY}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-zinc-500 text-center">
        * Prices are estimates. Summit Peptides discount is {summitDiscountPct}% off regular retail prices
        {isPremium ? " for Premium members" : " for free-tier users; Premium members get 30% off"}.
        For research and educational purposes only.
      </p>
    </div>
  );
}
